import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Lock, Sparkles } from 'lucide-react';
import type {
  AssetReviewItem,
  AssetReviewSnapshot,
  AssetReviewStage,
  MoodboardCandidate,
} from '@volley/crucible-ipc-types';
import { currentStageView } from './asset-stage-view.js';
import { candidateImageUrl, isPickOneStage } from './tv-steering-types.js';
import {
  finishGateSatisfied,
  isStyleLocked,
  isUnservable,
  styleAnchorStage,
  styleAnchorUnservable,
} from './art-style-lock.js';
import { useCrucibleClient } from '../transport/context.js';

/**
 * crucible#2868 (Stream A / AC-1) — THE PROSUMER GUIDED ART FLOW.
 *
 * The from-scratch art path for a FRESH game, worn in the Design tab's design
 * language so a prosumer moving Design → Art feels one continuous surface (NOT
 * the consumer `TvSteeringSurface` TV look). It is NOT a parallel review
 * implementation: it consumes the SAME staged-review machinery the consumer
 * surface does — the `currentStageView` projection (`asset-stage-view.ts`) and
 * the `art:*` IPC channels (`art:snapshot` poll + `art:approve` /
 * `:regenerate` / `:finish`, and `art:command` for the legacy pick-one stage
 * — crucible#3007 rewired these off the cloud-unreachable `asset-review:*`
 * invoke verbs; the `asset-review:closed`/`:ready` PUSH channels below are
 * unchanged). Only the skin and the layout differ.
 *
 * What it shows, one stage at a time (style-anchor-first, same as the consumer
 * flow, because later assets img2img-condition against the locked look and the
 * one-per-stage cost guard saves spend):
 *   - A left STAGE RAIL (mirrors DesignNav) — every stage with its status;
 *     the current stage is highlighted, approved stages carry a check, and
 *     stages downstream of the current one read as LOCKED (they cannot generate
 *     until the gate ahead of them is satisfied).
 *   - The current stage's ASSETS in the centre — per-asset Approve / Change
 *     (feedback → regenerate). The `currentStageView` projection already applies
 *     the crucible#2671 one-per-stage cost guard (solo-gate the stage's first
 *     asset, then batch the rest once one is approved); this surface renders that
 *     projection, it does not re-derive it.
 *   - The MANDATORY style-anchor gate — while the anchor stage is unsatisfied,
 *     a banner makes clear the look must be locked before anything else builds.
 *     The gate itself is enforced by the backend + the staged progression (the
 *     anchor is the first stage and `currentStageIndex` won't advance until it is
 *     satisfied); this surface presents that fact, it does not re-implement it.
 *
 * Poll / terminal handling mirrors `AssetReviewPanel` (the in-app review panel
 * this replaces as the fresh-game landing): a 1.5s snapshot poll, plus the
 * `asset-review:closed` / `:ready` pushes so a finish/abort/retry that races the
 * poll is observed as a fact rather than inferred from an ambiguous poll failure.
 */

/** Poll cadence for the snapshot while the surface is mounted (ms). */
const POLL_INTERVAL_MS = 1500;

// ── Labels (plain language; personality-free — this is prosumer UI) ─────────────

function stageStatusLabel(stage: AssetReviewStage): string {
  switch (stage.status) {
    case 'approved':
      return 'Locked';
    case 'review':
      return 'Your turn';
    case 'generating':
      return 'Generating';
    default:
      return 'Waiting';
  }
}

function assetLabel(asset: AssetReviewItem): string {
  return asset.id;
}

// ── Copy for an asset the engine REFUSED (crucible#3803) ────────────────────────
//
// Every user-facing string for the refused case lives here, in one block, so the
// wording is one edit rather than five. Plain language on purpose: "blocked" and
// "unservable" are engine vocabulary, and the old copy ("not ready") was the
// still-generating message, which is what made the user wait for something that
// was never coming.
//
// Each string says what happened AND what to do, because the whole bug was a
// surface that stated a requirement while disabling the only action that met it.

/** Thumbnail slot (rendered uppercase) where a still-generating asset says "not ready". */
const UNSERVABLE_SLOT_LABEL = "couldn't be made";

/** The card's explanatory line. Names the right kind of thing, and says the build continues. */
function unservableNote(asset: AssetReviewItem): string {
  const thing =
    asset.category === 'audio' ? 'sound' : asset.category === 'animation' ? 'animation' : 'image';
  return `We couldn't create this ${thing}. Try changing it. The rest of your art will still be made.`;
}

/** The style-gate banner when the anchor stage's every member was refused. */
const UNSERVABLE_GATE_LEAD = "We couldn't create your style image.";
const UNSERVABLE_GATE_DETAIL =
  'Ask for a change below to try again. Every other piece is drawn to match this look.';

/** Finish's disabled title in the same case. The gate is still mandatory — but
 *  "Lock the style before finishing" implies the user can, and here they cannot
 *  until a regeneration lands, so it names that instead. */
const UNSERVABLE_FINISH_TITLE = "We couldn't create your style image. Change it to try again.";

/** Feedback placeholder on a refused asset — the generic one ("same character,
 *  but…") assumes a take exists to iterate on. Says "optional" because on this
 *  branch it genuinely is. */
const UNSERVABLE_CHANGE_PLACEHOLDER = 'optional: e.g. simpler pose, plain background';

/** Submit label on a refused asset with nothing typed. "Make the change" reads as
 *  though notes are required; this one makes the bare-retry affordance legible. */
const UNSERVABLE_RETRY_SUBMIT_LABEL = 'Try again';

export interface ArtGuidedFlowProps {
  /** kebab-case game id whose staged art review this surface drives. */
  gameId: string;
  /** Fires once the engine reports the staged review is `done`. */
  onDone?: () => void;
}

export function ArtGuidedFlow({ gameId, onDone }: ArtGuidedFlowProps): React.JSX.Element {
  const client = useCrucibleClient();
  const [snapshot, setSnapshot] = useState<AssetReviewSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Per-asset busy flag (approve / regenerate / pick in flight). */
  const [busyId, setBusyId] = useState<string | null>(null);
  /** The asset currently showing its Change free-text box, keyed by id. */
  const [changeFor, setChangeFor] = useState<string | null>(null);
  const [changeText, setChangeText] = useState('');
  const [finishing, setFinishing] = useState(false);

  // Latch so `onDone` fires exactly once even though the poll keeps running.
  const doneFiredRef = useRef(false);
  // Last applied snapshot, so a dropped poll doesn't banner an error over a
  // review already on screen and healthy.
  const snapshotRef = useRef<AssetReviewSnapshot | null>(null);
  // Set the instant `asset-review:closed` decides the terminal state; once set,
  // the poll is a permanent no-op so a late tick can't revert `done` or wipe the
  // 'aborted' error (mirrors AssetReviewPanel's terminalRef).
  const terminalRef = useRef(false);
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  const apply = useCallback((next: AssetReviewSnapshot) => {
    snapshotRef.current = next;
    setSnapshot(next);
    setError(null);
    if (next.done && !doneFiredRef.current) {
      doneFiredRef.current = true;
      onDoneRef.current?.();
    }
  }, []);

  // Initial load + poll — the snapshot is the single source this surface renders
  // from; polling keeps the stage rail + per-asset state live as the engine
  // generates takes and advances stages.
  useEffect(() => {
    let cancelled = false;
    const tick = async (): Promise<void> => {
      if (terminalRef.current) return;
      try {
        const snap = await client.invoke('art:snapshot', { gameId });
        if (!cancelled && !terminalRef.current) apply(snap);
      } catch (err) {
        // Only surface a poll failure before the first snapshot lands; once one
        // is on screen a single dropped poll is transient (it re-polls shortly).
        if (!cancelled && !terminalRef.current && snapshotRef.current === null) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    };
    void tick();
    const handle = setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [apply, client, gameId]);

  // Terminal pushes — `asset-review:closed` (finished/aborted) removes the
  // poll's done-race, `asset-review:ready` un-latches a retry that re-launches a
  // fresh review for the same gameId. Same contract as AssetReviewPanel.
  useEffect(() => {
    const unsubClosed = client.subscribe('asset-review:closed', (event) => {
      if (event.gameId !== gameId) return;
      terminalRef.current = true;
      if (event.reason === 'finished') {
        if (snapshotRef.current) {
          apply({ ...snapshotRef.current, done: true });
        } else if (!doneFiredRef.current) {
          doneFiredRef.current = true;
          onDoneRef.current?.();
        }
      } else {
        setError(
          'The build was interrupted before this review finished. Restart the build to try again.',
        );
      }
    });
    const unsubReady = client.subscribe('asset-review:ready', (event) => {
      if (event.gameId !== gameId) return;
      terminalRef.current = false;
      doneFiredRef.current = false;
      setError(null);
    });
    return () => {
      unsubClosed();
      unsubReady();
    };
  }, [apply, client, gameId]);

  const onApprove = useCallback(
    async (id: string) => {
      setBusyId(id);
      try {
        const snap = await client.invoke('art:approve', {
          id,
          gameId,
        });
        apply(snap);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [apply, client, gameId],
  );

  const onRegenerate = useCallback(
    async (id: string, feedback: string) => {
      setBusyId(id);
      setChangeFor(null);
      setChangeText('');
      try {
        const snap = await client.invoke('art:regenerate', {
          id,
          feedback,
          gameId,
        });
        apply(snap);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [apply, client, gameId],
  );

  // Legacy pick-one (dead for post-crucible#2740 manifests, kept for older
  // snapshots): choosing a candidate goes through the SAME free-text command
  // channel the consumer surface uses — "use look A" — not a bespoke pick
  // payload, so there is exactly one backend path for a pick.
  const onPick = useCallback(
    async (candidate: MoodboardCandidate, label: string) => {
      setBusyId(candidate.id);
      try {
        const result = await client.invoke('art:command', {
          gameId,
          command: `use ${label.toLowerCase()}`,
          source: 'text',
        });
        if (result.snapshot) apply(result.snapshot);
        if (!result.ok && result.intent?.reason) setError(result.intent.reason);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [apply, client, gameId],
  );

  const onFinish = useCallback(async () => {
    setFinishing(true);
    try {
      const snap = await client.invoke('art:finish', { gameId });
      apply(snap);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setFinishing(false);
    }
  }, [apply, client, gameId]);

  const view = snapshot ? currentStageView(snapshot) : null;
  const stage = view?.stage ?? null;
  const pickOne = isPickOneStage(stage);
  const anchor = snapshot ? styleAnchorStage(snapshot) : null;
  const hasAnchor = anchor !== null;
  // The mandatory style gate is showing when the current stage IS the anchor and
  // it hasn't been passed yet — everything downstream depends on this look. With
  // no anchor stage (`!!anchor` is false) there is no gate, so this is false.
  const atStyleGate =
    !!snapshot && !!anchor && !isStyleLocked(snapshot) && stage?.index === anchor.index;
  // Finish readiness (crucible#2872): the mandatory style gate WHEN an anchor
  // exists, else "at least one piece kept" — never trapped disabled on a
  // no-anchor snapshot. The button still ANDs !finishing && !snapshot.done below.
  const readyToFinish = !!snapshot && finishGateSatisfied(snapshot);
  // crucible#3803 — the engine refused EVERY member of the still-unlocked anchor
  // stage, so no amount of waiting satisfies the mandatory gate. This does NOT
  // relax the gate (Finish stays disabled, Keep stays disabled); it changes what
  // the surface SAYS, and it is why Change is enabled on a refused asset below.
  const anchorUnservable = !!snapshot && styleAnchorUnservable(snapshot);

  return (
    <div
      className="flex h-full min-h-0 bg-crucible-bg text-crucible-fg"
      data-testid="art-guided-flow"
      data-game-id={gameId}
      data-current-stage={stage?.kind ?? ''}
      data-style-gate={atStyleGate ? 'true' : 'false'}
    >
      {/* ── Stage rail (mirrors the Design-tab left nav) ─────────────────────── */}
      <nav
        className="flex w-60 shrink-0 flex-col border-r border-crucible-border"
        data-testid="art-guided-stage-rail"
      >
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-crucible-muted">
            Art stages
          </h2>
          {view && view.stageCount > 0 ? (
            <ul className="space-y-0.5">
              {(snapshot!.stages ?? []).map((s) => {
                const current = s.index === view.stageNumber - 1;
                const approved = s.status === 'approved';
                // Downstream of the current stage → locked (can't generate until
                // the gate ahead of it is satisfied). This is what makes the
                // style-first ordering legible.
                const upcomingLocked = s.index > snapshot!.currentStageIndex;
                return (
                  <li key={s.kind}>
                    <div
                      data-testid={`art-guided-stage-${s.kind}`}
                      data-stage-status={s.status}
                      data-stage-current={current ? 'true' : 'false'}
                      className={`flex items-center gap-2 rounded px-2 py-1.5 text-sm ${
                        current
                          ? 'bg-crucible-accent/20 text-crucible-fg'
                          : approved
                            ? 'text-crucible-muted'
                            : upcomingLocked
                              ? 'text-crucible-muted/60'
                              : 'text-crucible-muted'
                      }`}
                    >
                      {approved ? (
                        <Check size={13} className="shrink-0 text-emerald-300" aria-hidden="true" />
                      ) : upcomingLocked ? (
                        <Lock size={12} className="shrink-0 opacity-70" aria-hidden="true" />
                      ) : (
                        <span
                          className={`size-1.5 shrink-0 rounded-full ${
                            current ? 'bg-crucible-accent-hover' : 'bg-current opacity-40'
                          }`}
                          aria-hidden="true"
                        />
                      )}
                      <span className="min-w-0 flex-1 truncate">{s.label}</span>
                      <span className="shrink-0 text-[10px] uppercase tracking-wide opacity-70">
                        {stageStatusLabel(s)}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="px-2 text-xs text-crucible-muted">Preparing your art stages…</p>
          )}
        </div>
        {view && view.stageCount > 0 && (
          <div className="shrink-0 border-t border-crucible-border p-3">
            <p className="px-1 text-[11px] text-crucible-muted" data-testid="art-guided-progress">
              Step {view.stageNumber} of {view.stageCount}
            </p>
          </div>
        )}
      </nav>

      {/* ── Centre — current stage ───────────────────────────────────────────── */}
      <section className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-crucible-border px-4 py-2.5">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-crucible-accent-hover">
              Build the look
            </p>
            <h2 className="truncate text-sm font-medium text-crucible-fg">
              {stage ? stage.label : 'Your game art'}
            </h2>
          </div>
          {snapshot && (
            <span
              className="shrink-0 rounded-full border border-crucible-border bg-crucible-bg-elevated px-2.5 py-1 text-[11px] text-crucible-muted"
              data-testid="art-guided-done-pill"
            >
              {snapshot.approved}/{snapshot.reviewable} kept{snapshot.done ? ' · done' : ''}
            </span>
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-auto p-4">
          {error && (
            <div
              className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300"
              role="alert"
            >
              {error}
            </div>
          )}

          {/* Mandatory style-anchor gate — everything else builds on this look. */}
          {atStyleGate && (
            <div
              className="mb-3 flex items-start gap-2.5 rounded-lg border border-crucible-accent/40 bg-crucible-accent/10 p-3"
              data-testid="art-guided-style-gate"
              data-anchor-unservable={anchorUnservable ? 'true' : 'false'}
            >
              <Sparkles
                size={15}
                className="mt-0.5 shrink-0 text-crucible-accent-hover"
                aria-hidden="true"
              />
              {anchorUnservable ? (
                <p className="text-xs leading-relaxed text-crucible-fg/90">
                  <span className="font-semibold">{UNSERVABLE_GATE_LEAD}</span>{' '}
                  {UNSERVABLE_GATE_DETAIL}
                </p>
              ) : (
                <p className="text-xs leading-relaxed text-crucible-fg/90">
                  <span className="font-semibold">Lock the style first.</span> Every other piece is
                  drawn to match this look, so keep or change it before the rest of the art is made.
                </p>
              )}
            </div>
          )}

          {!snapshot && !error && (
            <div
              className="flex items-center gap-3 py-6 text-sm text-crucible-muted"
              data-testid="art-guided-loading"
            >
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/20 border-t-crucible-accent" />
              Loading your generated art…
            </div>
          )}

          {/* Pick-one (legacy): choose one candidate look. */}
          {view && pickOne && stage?.candidates && (
            <div
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
              data-testid="art-guided-pick-row"
            >
              {stage.candidates.map((c, i) => {
                const label = `Look ${String.fromCharCode(65 + i)}`;
                return (
                  <GuidedCandidateCard
                    key={c.id}
                    candidate={c}
                    label={label}
                    busy={busyId === c.id}
                    onPick={() => void onPick(c, label)}
                  />
                );
              })}
            </div>
          )}

          {/* Every other stage: per-asset Approve / Change. */}
          {view && !pickOne && view.assets.length > 0 && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="art-guided-grid">
              {view.assets.map((asset) => (
                <GuidedAssetCard
                  key={asset.id}
                  asset={asset}
                  busy={busyId === asset.id}
                  changeOpen={changeFor === asset.id}
                  changeText={changeFor === asset.id ? changeText : ''}
                  onApprove={() => void onApprove(asset.id)}
                  onOpenChange={() => {
                    setChangeFor(asset.id);
                    setChangeText('');
                  }}
                  onCancelChange={() => {
                    setChangeFor(null);
                    setChangeText('');
                  }}
                  onChangeTextChange={setChangeText}
                  onSubmitChange={() => {
                    const fb = changeText.trim();
                    // crucible#3803 — a REFUSED asset may be retried with no notes.
                    // Verified against the engine, not assumed: asset-forge's
                    // `asset-service.ts` guards its revision-note interpolation on
                    // `feedback?.trim()` being non-empty, so `''` generates from the
                    // original prompt — a clean re-roll, not a malformed prompt. That
                    // is the right action for something that could not be made: the
                    // user has nothing to say about an image they never saw. Where a
                    // real take DOES exist, empty stays a no-op — there, "change"
                    // means "change this thing", and a silent re-roll is not what was
                    // asked for.
                    if (fb || isUnservable(asset)) void onRegenerate(asset.id, fb);
                  }}
                />
              ))}
            </div>
          )}

          {/* Current stage still generating (nothing landed yet). */}
          {view && !pickOne && view.assets.length === 0 && stage && !view.allStagesApproved && (
            <div
              className="flex items-center gap-3 py-6 text-sm text-crucible-muted"
              data-testid="art-guided-generating"
              role="status"
              aria-live="polite"
            >
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/20 border-t-crucible-accent" />
              Creating your {stage.label.toLowerCase()}…
            </div>
          )}
        </div>

        {/* Footer — finish the guided flow when happy. */}
        {snapshot && (
          <div className="flex items-center justify-between gap-3 border-t border-crucible-border px-4 py-2.5">
            <p className="min-w-0 truncate text-xs text-crucible-muted">
              {snapshot.done
                ? 'All set — finishing your game…'
                : 'Keep each piece or ask for a change. Finish when the look is right.'}
            </p>
            <button
              type="button"
              data-testid="art-guided-finish"
              disabled={finishing || snapshot.done || !readyToFinish}
              title={
                readyToFinish
                  ? 'Finish the art'
                  : anchorUnservable
                    ? UNSERVABLE_FINISH_TITLE
                    : hasAnchor
                      ? 'Lock the style before finishing'
                      : 'Keep at least one piece before finishing'
              }
              onClick={() => void onFinish()}
              className="shrink-0 rounded-lg bg-crucible-accent px-4 py-2 text-sm font-medium text-black transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {finishing || snapshot.done ? 'Finishing…' : 'Finish art'}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

/** One asset card: thumbnail + label/status + the Approve / Change verdict row.
 *  Design-skinned sibling of AssetReviewPanel's AssetCard. */
function GuidedAssetCard({
  asset,
  busy,
  changeOpen,
  changeText,
  onApprove,
  onOpenChange,
  onCancelChange,
  onChangeTextChange,
  onSubmitChange,
}: {
  asset: AssetReviewItem;
  busy: boolean;
  changeOpen: boolean;
  changeText: string;
  onApprove: () => void;
  onOpenChange: () => void;
  onCancelChange: () => void;
  onChangeTextChange: (v: string) => void;
  onSubmitChange: () => void;
}): React.JSX.Element {
  const approved = asset.status === 'approved';
  // crucible#3803 — the engine REFUSED this asset. It is not "still generating",
  // and the two rendered identically before this, which is why the surface was a
  // dead end. A refused asset can still be CHANGED (a regeneration is exactly the
  // right response to a failed generation) but never KEPT — there is nothing to
  // approve. `reviewable` cannot make this distinction: it is false for a refused
  // asset and a not-yet-generated one alike.
  const unservable = isUnservable(asset);
  const canChange = asset.reviewable || unservable;
  return (
    <div
      className="flex flex-col gap-2 rounded-xl border border-crucible-border bg-crucible-bg-elevated p-3"
      data-testid={`art-guided-card-${asset.id}`}
      data-asset-status={asset.status}
    >
      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-white/5">
        {asset.category !== 'audio' && asset.imageUrl ? (
          <img
            src={asset.imageUrl}
            alt={assetLabel(asset)}
            className="h-full w-full object-contain"
            style={{
              backgroundImage:
                'linear-gradient(45deg,#222 25%,transparent 25%),linear-gradient(-45deg,#222 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#222 75%),linear-gradient(-45deg,transparent 75%,#222 75%)',
              backgroundSize: '16px 16px',
              backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
            }}
          />
        ) : (
          <div
            className={`flex h-full w-full items-center justify-center text-xs uppercase tracking-wide ${
              unservable ? 'text-amber-200/90' : 'text-crucible-muted'
            }`}
          >
            {unservable
              ? UNSERVABLE_SLOT_LABEL
              : asset.category === 'audio'
                ? '♪ audio'
                : asset.reviewable
                  ? 'no preview'
                  : 'not ready'}
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-crucible-accent" />
          </div>
        )}
        {approved && (
          <span className="absolute right-1.5 top-1.5 rounded-full bg-emerald-500/90 px-2 py-0.5 text-[10px] font-semibold text-black">
            ✓ kept
          </span>
        )}
      </div>

      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-crucible-fg">{assetLabel(asset)}</p>
        <p className="truncate text-[11px] uppercase tracking-wide text-crucible-muted">
          {asset.category}
          {asset.status !== 'approved' ? ` · ${asset.status}` : ''}
        </p>
      </div>

      {/* crucible#3803 — say what happened and what to do. `role="status"` (not
          `alert`): a refused asset is a state of THIS card, not an app error, and
          it arrives on a poll tick, so a polite live region announces it. */}
      {unservable && (
        <div
          className="rounded-md border border-amber-400/40 bg-amber-500/10 px-2 py-1.5 text-xs text-amber-100"
          data-testid={`art-guided-unservable-${asset.id}`}
          role="status"
        >
          {unservableNote(asset)}
        </div>
      )}

      {asset.bgRemovalDefect && (
        <div
          className="rounded-md border border-red-400/40 bg-red-500/15 px-2 py-1.5 text-xs font-medium text-red-200"
          role="alert"
        >
          Background not removed
        </div>
      )}

      {changeOpen ? (
        <div className="flex flex-col gap-2">
          <textarea
            autoFocus
            value={changeText}
            onChange={(e) => onChangeTextChange(e.target.value)}
            placeholder={
              unservable
                ? UNSERVABLE_CHANGE_PLACEHOLDER
                : 'e.g. same character, but warmer light and a calmer pose'
            }
            data-testid={`art-guided-change-input-${asset.id}`}
            className="h-16 w-full resize-none rounded-lg border border-crucible-border bg-crucible-bg p-2 text-xs text-crucible-fg placeholder:text-crucible-muted focus:border-crucible-accent focus:outline-none"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || (!changeText.trim() && !unservable)}
              onClick={onSubmitChange}
              data-testid={`art-guided-change-submit-${asset.id}`}
              className="flex-1 rounded-lg bg-crucible-accent px-2 py-1.5 text-xs font-medium text-black transition hover:brightness-110 disabled:opacity-40"
            >
              {unservable && !changeText.trim() ? UNSERVABLE_RETRY_SUBMIT_LABEL : 'Make the change'}
            </button>
            <button
              type="button"
              onClick={onCancelChange}
              className="rounded-lg bg-white/10 px-2 py-1.5 text-xs text-crucible-muted transition hover:bg-white/15"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy || approved || !asset.reviewable}
            onClick={onApprove}
            data-testid={`art-guided-approve-${asset.id}`}
            className="flex-1 rounded-lg bg-white/10 px-2 py-1.5 text-xs font-medium text-crucible-fg transition hover:bg-emerald-500/20 disabled:opacity-40"
          >
            {approved ? 'Kept' : 'Keep'}
          </button>
          <button
            type="button"
            disabled={busy || !canChange}
            onClick={onOpenChange}
            data-testid={`art-guided-change-${asset.id}`}
            className="flex-1 rounded-lg bg-white/10 px-2 py-1.5 text-xs font-medium text-crucible-fg transition hover:bg-white/15 disabled:opacity-40"
          >
            ✏ Change
          </button>
        </div>
      )}
    </div>
  );
}

/** One pick-one candidate (legacy stage): click the tile to choose that look. */
function GuidedCandidateCard({
  candidate,
  label,
  busy,
  onPick,
}: {
  candidate: MoodboardCandidate;
  label: string;
  busy: boolean;
  onPick: () => void;
}): React.JSX.Element {
  const url = candidateImageUrl(candidate);
  const picked = candidate.status === 'picked';
  const dropped = candidate.status === 'discarded';
  return (
    <button
      type="button"
      disabled={busy || dropped}
      onClick={onPick}
      data-testid={`art-guided-candidate-${candidate.id}`}
      data-candidate-state={picked ? 'picked' : dropped ? 'dropped' : 'staging'}
      aria-label={`Choose ${label}`}
      className={`flex flex-col overflow-hidden rounded-xl border text-left transition disabled:cursor-not-allowed ${
        picked
          ? 'border-crucible-accent ring-2 ring-crucible-accent/50'
          : 'border-crucible-border hover:border-crucible-accent/50'
      } ${dropped ? 'opacity-30' : ''}`}
    >
      <div className="relative aspect-video w-full bg-white/5">
        {url ? (
          <img src={url} alt={label} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs uppercase text-crucible-muted">
            {candidate.status === 'generating' ? 'generating…' : 'no preview'}
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-crucible-accent" />
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <span className="text-sm font-medium text-crucible-fg">{label}</span>
        <span className="text-[10px] uppercase tracking-wide text-crucible-muted">
          {picked ? 'Picked' : 'Choose'}
        </span>
      </div>
    </button>
  );
}
